import { useState, useEffect, useRef, useCallback } from 'react';
import { processImageData, convertIpfsUrl, getNextGatewayUrl, isIpfsUrl, isIpfsCid } from '../utils/ipfsUtils';

interface UseIpfsImageOptions {
  maxRetries?: number;
  onError?: (error: string) => void;
  onSuccess?: (url: string) => void;
}

export function useIpfsImage(
  imageUrl: string | undefined,
  options: UseIpfsImageOptions = {}
) {
  const { maxRetries = 2, onError, onSuccess } = options;
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isSuccessfullyLoaded, setIsSuccessfullyLoaded] = useState(false);
  const currentImageUrlRef = useRef<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced URL setter to prevent rapid changes
  const setCurrentUrlDebounced = useCallback((url: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentUrl(url);
    }, 100);
  }, []);

  useEffect(() => {
    if (!imageUrl) {
      setCurrentUrl('');
      setError(null);
      setIsLoading(false);
      setHasLoadedOnce(false);
      setIsSuccessfullyLoaded(false);
      currentImageUrlRef.current = '';
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      return;
    }

    // If we've already successfully loaded this image, don't change it
    if (isSuccessfullyLoaded && currentImageUrlRef.current === imageUrl) {
      return;
    }

    // Reset state for new image
    setError(null);
    setIsLoading(true);
    setRetryCount(0);
    setHasLoadedOnce(false);
    setIsSuccessfullyLoaded(false);
    currentImageUrlRef.current = imageUrl;

    // Process the image URL using the processImageData function
    const processedUrl = processImageData(imageUrl);
    
    // Check if it's an IPFS CID
    const isIpfsId = isIpfsCid(imageUrl);
    
    // If it's not an IPFS URL, use it directly
    if (!isIpfsUrl(processedUrl)) {
      setCurrentUrlDebounced(processedUrl);
      setIsLoading(false);
      setHasLoadedOnce(true);
      setIsSuccessfullyLoaded(true);
      onSuccess?.(processedUrl);
      return;
    }

    // For IPFS URLs, try multiple gateways with a simpler approach
    const tryGateway = (gatewayIndex: number) => {
      if (gatewayIndex >= maxRetries) {
        setIsLoading(false);
        const errorMsg = `Failed to load image after ${maxRetries} attempts`;
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      const url = convertIpfsUrl(processedUrl, gatewayIndex);
      setCurrentUrlDebounced(url);
      
      const testImage = new Image();
      let timeoutId: NodeJS.Timeout;
      
      testImage.onload = () => {
        clearTimeout(timeoutId);
        setIsLoading(false);
        setError(null);
        setHasLoadedOnce(true);
        setIsSuccessfullyLoaded(true);
        onSuccess?.(url);
      };
      
      testImage.onerror = () => {
        clearTimeout(timeoutId);
        console.warn(`Failed to load image from gateway ${gatewayIndex + 1}:`, url);
        // Only try next gateway if we haven't successfully loaded yet
        if (!isSuccessfullyLoaded) {
          setTimeout(() => {
            tryGateway(gatewayIndex + 1);
          }, 200); // Increased delay to prevent rapid switching
        }
      };
      
      // Set a timeout to prevent hanging
      timeoutId = setTimeout(() => {
        testImage.onerror = null; // Prevent multiple error calls
        testImage.onload = null;
        console.warn(`Image load timeout for gateway ${gatewayIndex + 1}:`, url);
        // Only try next gateway if we haven't successfully loaded yet
        if (!isSuccessfullyLoaded) {
          setTimeout(() => {
            tryGateway(gatewayIndex + 1);
          }, 200); // Increased delay to prevent rapid switching
        }
      }, 5000); // 5 second timeout
      
      testImage.src = url;
    };

    // Start with the first gateway
    tryGateway(0);
  }, [imageUrl, maxRetries, onError, onSuccess, isSuccessfullyLoaded, setCurrentUrlDebounced]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    url: currentUrl,
    isLoading,
    error,
    retryCount,
    hasLoadedOnce,
    isSuccessfullyLoaded
  };
} 